// GPU-instanced impact particles. Simple sprite billboards with toon-ish color + gravity.

import * as THREE from 'three';

interface Particle {
  alive: boolean;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  color: THREE.Color;
  scale: number;
}

const MAX = 256;

export class Particles {
  readonly mesh: THREE.InstancedMesh;
  private parts: Particle[] = [];
  private mat: THREE.MeshBasicMaterial;
  private dummy = new THREE.Object3D();
  private next = 0;

  constructor() {
    const geo = new THREE.PlaneGeometry(0.18, 0.18);
    this.mat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide
    });
    this.mesh = new THREE.InstancedMesh(geo, this.mat, MAX);
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = MAX;
    const colors = new Float32Array(MAX * 3);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < MAX; i++) {
      this.parts.push({
        alive: false,
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        life: 0,
        maxLife: 0,
        color: new THREE.Color(),
        scale: 0
      });
    }
  }

  burst(pos: THREE.Vector3, count: number, baseColor: THREE.Color, strength: number): void {
    for (let i = 0; i < count; i++) {
      const p = this.parts[this.next % MAX]!;
      this.next++;
      p.alive = true;
      p.pos.copy(pos);
      const a = Math.random() * Math.PI * 2;
      const up = Math.random() * 0.7 + 0.4;
      const speed = 2 + Math.random() * strength;
      p.vel.set(Math.cos(a) * speed, up * speed, Math.sin(a) * speed);
      p.life = 0;
      p.maxLife = 0.45 + Math.random() * 0.4;
      p.color.copy(baseColor).offsetHSL(Math.random() * 0.1 - 0.05, 0, Math.random() * 0.1);
      p.scale = 0.4 + Math.random() * 0.7;
    }
  }

  update(dt: number, camera: THREE.Camera): void {
    // Track the highest slot index that still holds a live particle this
    // frame. Setting `mesh.count = highest + 1` means the GPU only draws
    // up to the live tail instead of all 256 instances every frame, which
    // matters because dead-but-parked instances were still being submitted
    // and rasterised (even at scale 0 / off-screen). Buffer uploads are
    // unchanged — the upload size is bound to the underlying typed array,
    // not `mesh.count`.
    let highest = -1;
    for (let i = 0; i < MAX; i++) {
      const p = this.parts[i]!;
      if (!p.alive) continue;
      p.life += dt;
      if (p.life >= p.maxLife) {
        p.alive = false;
        // Park the just-died slot off-screen so any later draw of this
        // index (until something respawns into it) renders nothing.
        this.dummy.position.set(0, -1000, 0);
        this.dummy.scale.setScalar(0);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
        continue;
      }
      // Integrate
      p.vel.y -= 9.0 * dt;
      p.vel.multiplyScalar(Math.exp(-3.5 * dt));
      p.pos.addScaledVector(p.vel, dt);
      const t = p.life / p.maxLife;
      const fade = 1 - t * t;
      this.dummy.position.copy(p.pos);
      this.dummy.lookAt(camera.position);
      this.dummy.scale.setScalar(p.scale * (0.6 + 0.7 * (1 - t)));
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
      // color attribute (additive intensity via brightness)
      if (this.mesh.instanceColor) {
        const c = this.mesh.instanceColor.array as Float32Array;
        c[i * 3 + 0] = p.color.r * fade;
        c[i * 3 + 1] = p.color.g * fade;
        c[i * 3 + 2] = p.color.b * fade;
      }
      if (i > highest) highest = i;
    }
    this.mesh.count = highest + 1;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) (this.mesh.instanceColor as THREE.InstancedBufferAttribute).needsUpdate = true;
  }
}
